import React, { useEffect, useState } from "react";
import { useGetDockPeakActivityQuery } from "../../api/vmsSlice";

/**
 * Hourly movement counts change by the hour, so this refreshes far more slowly than the live board
 * and is deliberately not wired to dock-operations-changed: one truck arriving fires four camera
 * events, which would run the aggregation four times to redraw an identical chart.
 */
const PEAK_POLL_MS = 300000;
const PEAK_RANGES = [
	{ key: "today", label: "Today" },
	{ key: "week", label: "Last 7 days" },
];
// Naming more than a few tied hours turns the caption into a list, so the rest become a count.
const PEAK_NAMED_TIES = 3;
// Sequential ramp, light to dark, single hue: darker only ever means busier.
const PEAK_HEAT_STEPS = ["#FFF0E1", "#FFD6B0", "#FFB273", "#FB8A34", "#C2410C"];

// Keeps "1 arrival" from reading as "1 arrivals".
function arrivalWord(count) {
	return count === 1 ? "arrival" : "arrivals";
}

// Formats an hour bucket as a local clock label; the backend already grouped by plant-local hour.
function hourLabel(hour, compact = false) {
	return `${hour % 12 || 12}${compact ? "" : ":00"} ${hour < 12 ? "AM" : "PM"}`;
}

// Picks a ramp step for one cell, keeping zero visually separate from the lightest non-zero step.
function heatStep(movements, maxMovements) {
	if (!movements) return "transparent";
	if (!maxMovements) return PEAK_HEAT_STEPS[0];
	const ratio = movements / maxMovements;
	const index = Math.min(PEAK_HEAT_STEPS.length - 1, Math.floor(ratio * PEAK_HEAT_STEPS.length));
	return PEAK_HEAT_STEPS[index];
}

// Renders the 24 hour column chart, emphasising the peak hours rather than shading every bar.
function PeakColumns({ buckets, maxMovements, peakHourSet, currentHour }) {
	return (
		<div className="dock-peak-columns">
			{buckets.map((bucket) => {
				// Every hour tied for busiest is emphasised; highlighting only one would hide a peak.
				const isPeak = bucket.movements > 0 && peakHourSet.has(bucket.hour);
				const heightPercent = maxMovements ? (bucket.movements / maxMovements) * 100 : 0;
				// An hour that has not arrived yet is not an empty hour, so it is drawn differently.
				const isFuture = Number.isFinite(currentHour) && bucket.hour > currentHour;
				return (
					<div className={`dock-peak-column${isFuture ? " dock-peak-column-future" : ""}`} key={bucket.hour}>
						<div className="dock-peak-column-track">
							{isPeak && <span className="dock-peak-column-value">{bucket.movements}</span>}
							<div
								className={`dock-peak-bar${isPeak ? " dock-peak-bar-peak" : ""}`}
								style={{ height: `${heightPercent}%` }}
								tabIndex={0}
								role="img"
								aria-label={isFuture ? `${hourLabel(bucket.hour)}, not yet reached` : `${hourLabel(bucket.hour)}, ${bucket.movements} ${arrivalWord(bucket.movements)}`}
							>
								<span className="dock-peak-tooltip">
									{hourLabel(bucket.hour)} · {isFuture ? "not yet reached" : `${bucket.movements} ${arrivalWord(bucket.movements)}`}
								</span>
							</div>
						</div>
						<span className="dock-peak-hour-label">{bucket.hour % 3 === 0 ? hourLabel(bucket.hour, true) : ""}</span>
					</div>
				);
			})}
		</div>
	);
}

/**
 * Renders the date by hour grid, the one shape that can show both dimensions at once.
 * The backend sends rows oldest first, so today lands on the bottom row by DOM order alone.
 */
function PeakHeatmap({ rows, maxMovements, currentHour }) {
	return (
		<div className="dock-peak-heatmap">
			{rows.map((row) => (
				<div className={`dock-peak-heat-row${row.isToday ? " dock-peak-heat-row-today" : ""}`} key={row.date}>
					<span className="dock-peak-day-label" title={row.date}>
						{row.label}{row.isToday ? <em> · today</em> : null}
					</span>
					{row.hours.map((movements, hour) => {
						// Only today's row can hold hours that have not arrived yet.
						const isFuture = row.isToday && Number.isFinite(currentHour) && hour > currentHour;
						return (
							<div
								/* eslint-disable-next-line react/no-array-index-key */
								key={hour}
								className={`dock-peak-cell${isFuture ? " dock-peak-cell-future" : ""}`}
								style={isFuture ? undefined : { backgroundColor: heatStep(movements, maxMovements) }}
								tabIndex={0}
								role="img"
								aria-label={isFuture ? `${row.label}, ${hourLabel(hour)}, not yet reached` : `${row.label}, ${hourLabel(hour)}, ${movements} ${arrivalWord(movements)}`}
							>
								<span className="dock-peak-tooltip">
									{row.label} {hourLabel(hour)} · {isFuture ? "not yet reached" : `${movements} ${arrivalWord(movements)}`}
								</span>
							</div>
						);
					})}
				</div>
			))}
			<div className="dock-peak-heat-row dock-peak-heat-axis">
				<span className="dock-peak-day-label" />
				{Array.from({ length: 24 }, (unused, hour) => (
					/* eslint-disable-next-line react/no-array-index-key */
					<span key={hour}>{hour % 3 === 0 ? hourLabel(hour, true) : ""}</span>
				))}
			</div>
		</div>
	);
}

// Shows Bay arrivals per hour, for every Bay or one selected Bay, so operators can see which
// windows carry the dock load.
export default function PeakActivity({ plantId, canView }) {
	const [range, setRange] = useState("today");
	// null means every Bay, which is the default view; the key is omitted so that request stays
	// identical to the all-Bays one and RTK Query caches each range/Bay pair separately.
	const [selectedBay, setSelectedBay] = useState(null);
	const query = useGetDockPeakActivityQuery(
		{ plantId, range, ...(selectedBay ? { checkpointNumbers: [selectedBay] } : {}) },
		{ skip: !plantId || !canView, pollingInterval: PEAK_POLL_MS },
	);
	const response = query.data;
	const data = response?.data;
	const bays = data?.bays || [];
	// Plant configuration can change under a long-open tab, and a chip the backend would now reject
	// must not stay pressed.
	useEffect(() => {
		if (selectedBay && bays.length && !bays.some((bay) => bay.checkpointNumber === selectedBay)) {
			setSelectedBay(null);
		}
	}, [bays, selectedBay]);
	const selectedBayName = data?.selectedBays?.length === 1 ? data.selectedBays[0].displayName : null;
	const scopeLabel = selectedBayName || "All bays";
	const maxMovements = Number(data?.maxMovements) || 0;
	const peak = data?.peak;
	const quietest = data?.quietest;
	const peakHours = data?.peakHours || [];
	const quietestHours = data?.quietestHours || [];
	// Only meaningful for the today columns, where an hour identifies a bucket on its own.
	const peakHourSet = new Set(range === "today" ? peakHours.map((point) => point.hour) : []);
	// The plant's current clock hour, from the response — never from the viewer's own clock, which
	// may be in a different timezone from the plant.
	const currentHour = Number.isFinite(data?.currentHour) ? data.currentHour : null;

	// Every figure in the caption is measured, so nothing here can drift from what the chart shows.
	// The scope comes from the response, never from local chip state, so it cannot name a Bay the
	// backend did not actually count.
	const period = range === "week" ? "the last 7 days" : "today";
	let caption = selectedBayName
		? `No arrivals recorded at ${selectedBayName} ${period}.`
		: `No Bay arrivals were recorded ${period}.`;
	if (data?.totalMovements && peak) {
		const whenOf = (point) => (range === "week" ? `${point.label} ${hourLabel(point.hour)}` : hourLabel(point.hour));
		// Lists every tied hour up to the cap, then counts the remainder rather than running long.
		const listWhen = (points) => {
			const named = points.slice(0, PEAK_NAMED_TIES).map(whenOf);
			const hidden = points.length - named.length;
			if (hidden > 0) return `${named.join(", ")} and ${hidden} more`;
			if (named.length === 1) return named[0];
			return `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;
		};
		const where = selectedBayName ? `${selectedBayName} busiest` : "Busiest";
		const scopeTotal = selectedBayName ? `${data.totalMovements} at this Bay` : `${data.totalMovements} across all bays`;
		// "each" and "together" only apply to a tie, so both phrasings key off the tie count.
		const tied = peakHours.length > 1;
		const together = tied ? "together " : "";
		caption = `${where} at ${listWhen(peakHours)} with ${peak.movements} ${arrivalWord(peak.movements)}${tied ? " each" : ""}`
			+ ` — ${together}${data.peakSharePercent}% of ${scopeTotal} ${period}.`;
		// quietest is the quietest WORKED hour, and the backend sends null when the window is flat,
		// so this clause simply disappears rather than restating the peak.
		if (quietest) {
			caption += ` Quietest working hour${quietestHours.length > 1 ? "s" : ""} ${listWhen(quietestHours)}`
				+ ` with ${quietest.movements} ${arrivalWord(quietest.movements)}${quietestHours.length > 1 ? " each" : ""}.`;
		}
		// totalHours counts elapsed hours only, so "so far" is accurate mid-day and harmless once
		// the day is complete.
		if (data.idleHours) {
			caption += ` ${data.idleHours} of ${data.totalHours} hours so far had no arrivals.`;
		}
	}

	if (!canView) return null;

	return (
		<section className="dock-peak-panel" aria-label="Peak activity times">
			<div className="dock-peak-head">
				<div>
					<h2>Peak activity times</h2>
					<span>
						Bay arrivals per hour · {scopeLabel}
						{range === "today" && data?.dateLabel ? ` · ${data.dateLabel}` : ""}
						{response?.timezone ? ` · ${response.timezone.replace("_", " ")}` : ""}
					</span>
				</div>
				<div className="dock-filter-chips" role="group" aria-label="Peak activity range">
					{PEAK_RANGES.map((option) => (
						<button
							key={option.key}
							type="button"
							className="dock-chip"
							aria-pressed={range === option.key}
							onClick={() => setRange(option.key)}
						>
							{option.label}
						</button>
					))}
				</div>
			</div>

			{/* Rendered from the response, so the row can never offer a Bay the backend would reject. */}
			{bays.length > 1 && (
				<div className="dock-filter-chips dock-peak-bays" role="group" aria-label="Filter by Bay">
					<button
						type="button"
						className="dock-chip"
						aria-pressed={selectedBay === null}
						onClick={() => setSelectedBay(null)}
					>
						All bays
					</button>
					{bays.map((bay) => (
						<button
							key={bay.checkpointNumber}
							type="button"
							className="dock-chip"
							aria-pressed={selectedBay === bay.checkpointNumber}
							onClick={() => setSelectedBay(bay.checkpointNumber)}
						>
							{bay.displayName}
						</button>
					))}
				</div>
			)}

			{(query.error || response?.error) && (
				<div className="dock-message error">{response?.message || "Peak activity could not be loaded."}</div>
			)}

			{query.isLoading || query.isFetching ? (
				<div className="dock-peak-placeholder">Loading Bay arrivals…</div>
			) : (
				<>
					{range === "week" && data?.rows && <PeakHeatmap rows={data.rows} maxMovements={maxMovements} currentHour={currentHour} />}
					{range === "today" && data?.buckets && (
						<PeakColumns buckets={data.buckets} maxMovements={maxMovements} peakHourSet={peakHourSet} currentHour={currentHour} />
					)}
					<div className="dock-peak-footer">
						{range === "week" && (
							<div className="dock-peak-scale">
								Quieter
								<span className="dock-peak-scale-bar">
									{PEAK_HEAT_STEPS.map((step) => <i key={step} style={{ backgroundColor: step }} />)}
								</span>
								Busier
							</div>
						)}
						<p className="dock-peak-caption">{caption}</p>
					</div>
				</>
			)}
		</section>
	);
}
